// File: frontend/src/services/adminAuditLogService.ts

import { apiClient } from "./apiClient";
import { PageResponse } from "../models/PaginationModel";

// Mirror of backend AuditAction enum
export type AuditAction =
  | "DELETE_ENTRY"
  | "RESTORE_ENTRY"
  | "EDIT_ENTRY"
  | "HARD_DELETE_ENTRY"
  | "CREATE_USER"
  | "DELETE_USER"
  // Physical removal of the user row itself (irreversible)
  | "HARD_DELETE_USER"
  | "BAN_USER"
  | "UNBAN_USER"
  | "SUSPEND_USER"
  | "UNSUSPEND_USER"
  | "CHANGE_USER_ROLE"
  | "CHANGE_USER_STATUS"
  | "CHANGE_USER_PASSWORD"
  | "CHANGE_USERNAME"
  | "CHANGE_USER_EMAIL"
  | "SET_VERIFIED"
  | "EDIT_BIO"
  | "DELETE_BIO"
  | "BLOCK_BIO"
  | "UNBLOCK_BIO"
  | "BAN_IP"
  | "UNBAN_IP"
  | "DELETE_IP_BAN"
  // Topic lock/unlock actions — applied to match, club, team, or person pages
  | "LOCK_TOPIC"
  | "UNLOCK_TOPIC"
  // Topic merge / entry move actions
  | "MERGE_TOPIC"
  | "MOVE_ENTRY"
  // Physical removal of a custom topic ("gündem") and everything under it
  | "HARD_DELETE_TOPIC";

// Mirror of backend AuditTargetType enum
export type AuditTargetType = "ENTRY" | "USER" | "IP" | "MATCH" | "CLUB" | "TEAM" | "PERSON" | "CUSTOM_TOPIC";

export interface AuditLogEntry {
  id: number;
  actorId: number;
  actorUsername: string;
  action: AuditAction;
  targetType: AuditTargetType;
  targetId: number | null;
  targetLabel: string | null;
  detail: string | null;
  createdAt: string; // ISO date-time string (no timezone suffix from backend)
}

export interface AuditLogFilters {
  actorSearch?: string;
  action?: AuditAction | "";
  targetType?: AuditTargetType | "";
  startDate?: string; // ISO date-time string
  endDate?: string;   // ISO date-time string
}

/**
 * Fetch a paginated, filtered page of audit log records.
 * All filter fields are optional.
 */
export const getAuditLogs = async (
  page: number,
  size: number,
  filters: AuditLogFilters
): Promise<PageResponse<AuditLogEntry>> => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());

  if (filters.actorSearch)  params.append("actorSearch", filters.actorSearch);
  if (filters.action)       params.append("action",      filters.action);
  if (filters.targetType)   params.append("targetType",  filters.targetType);
  if (filters.startDate)    params.append("startDate",   filters.startDate);
  if (filters.endDate)      params.append("endDate",     filters.endDate);

  return apiClient.get<PageResponse<AuditLogEntry>>(
    `/api/admin/audit-logs?${params.toString()}`
  );
};